import React, { useState, useEffect, useRef } from 'react';
import { updateGame } from '../services/firebase';
import { getCombinedWords } from '../data/wordPacks';
import toast from 'react-hot-toast';

const WordSelectionPhase = ({ game, userId, onComplete }) => {
  const [wordOptions, setWordOptions] = useState([]);
  const [customWord, setCustomWord] = useState('');
  const [selectedWord, setSelectedWord] = useState(null);
  const [votes, setVotes] = useState({});
  const [timeLeft, setTimeLeft] = useState(30);
  const [isTieBreaker, setIsTieBreaker] = useState(false);
  const hasProcessedWinner = useRef(false);

  const allowCustomWords = game?.settings?.allowCustomWords;
  const wordPacks = game?.settings?.wordPacks || ['en_allAges'];

  // Get the word-selecting team dynamically
  const wordSelectingTeam = game?.currentRoundData?.wordSelectingTeam;
  const wordSelectingTeamLabel = wordSelectingTeam === 'teamA' ? 'A' : 'B';
  const teamMembers = game?.teams?.[wordSelectingTeam] || [];

  // Initialize word options and sync with Firebase
  useEffect(() => {
    // Check if words are already in Firebase
    if (game?.currentRoundData?.wordOptions) {
      setWordOptions(game.currentRoundData.wordOptions);
    } else {
      // Host initializes the words
      const words = getCombinedWords(wordPacks, 5);
      setWordOptions(words);

      // Save to Firebase so all players see the same options
      updateGame(game.gameId, {
        'currentRoundData.wordOptions': words
      });
    }
  }, []);

  // Sync word options when they change in Firebase
  useEffect(() => {
    if (game?.currentRoundData?.wordOptions) {
      setWordOptions(game.currentRoundData.wordOptions);
    }
  }, [game?.currentRoundData?.wordOptions]);

  // Sync votes from Firebase and check for user's existing vote
  useEffect(() => {
    if (game?.currentRoundData?.votes) {
      setVotes(game.currentRoundData.votes);

      // If this user already has a vote in Firebase, reflect it locally
      if (game.currentRoundData.votes[userId]) {
        setSelectedWord(game.currentRoundData.votes[userId]);
      }
    }
  }, [game?.currentRoundData?.votes, userId]);

  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) {
      handleTimerExpiry();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle vote selection - immediately updates Firebase
  const handleVote = async (word) => {
    setSelectedWord(word);

    try {
      // Immediately update vote in Firebase (allows changing votes)
      const newVotes = {
        ...votes,
        [userId]: word
      };

      await updateGame(game.gameId, {
        'currentRoundData.votes': newVotes
      });
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to submit vote');
    }
  };

  const handleAddCustomWord = async () => {
    if (!customWord.trim()) {
      toast.error('Please enter a word');
      return;
    }

    if (wordOptions.length >= 8) {
      toast.error('Maximum 8 options allowed');
      return;
    }

    const newWord = customWord.trim();
    const newOptions = [...wordOptions, newWord];

    try {
      // Update Firebase so all team members see the new word
      await updateGame(game.gameId, {
        'currentRoundData.wordOptions': newOptions
      });

      setCustomWord('');
      toast.success(`"${newWord}" added for everyone to vote on!`);
    } catch (error) {
      console.error('Error adding custom word:', error);
      toast.error('Failed to add word');
    }
  };

  const handleTimerExpiry = async () => {
    // Prevent multiple executions
    if (hasProcessedWinner.current) return;

    // Count votes
    const voteCounts = {};
    Object.values(votes).forEach(word => {
      voteCounts[word] = (voteCounts[word] || 0) + 1;
    });

    // If no votes at all, pick the first word option
    if (Object.keys(voteCounts).length === 0) {
      hasProcessedWinner.current = true;
      await selectWinningWord(wordOptions[0] || 'Mystery Word');
      return;
    }

    // Find max vote count
    const maxVotes = Math.max(...Object.values(voteCounts));

    // Find all words with max votes (potential tie)
    const topWords = Object.entries(voteCounts)
      .filter(([word, count]) => count === maxVotes)
      .map(([word]) => word);

    if (topWords.length === 1) {
      // Clear winner - proceed
      hasProcessedWinner.current = true;
      await selectWinningWord(topWords[0]);
    } else {
      // TIE! Extend time for tie-breaker
      if (!isTieBreaker) {
        setIsTieBreaker(true);
        setTimeLeft(15); // Give 15 more seconds
        toast('🔄 Tie! Keep voting to break the tie!', {
          icon: '⚖️',
          duration: 3000
        });
      } else {
        // Second tie-breaker expired - randomly select from tied words
        hasProcessedWinner.current = true;
        const randomWinner = topWords[Math.floor(Math.random() * topWords.length)];
        toast(`Random selection from tie: "${randomWinner}"`, {
          icon: '🎲',
          duration: 3000
        });
        await selectWinningWord(randomWinner);
      }
    }
  };

  const selectWinningWord = async (winningWord) => {
    try {
      // Update game with winning word
      await updateGame(game.gameId, {
        'currentRoundData.selectedWord': winningWord
      });

      // Check if performer is already selected, if so, transition to performing
      if (game?.currentRoundData?.performerId) {
        await updateGame(game.gameId, {
          gamePhase: 'performing'
        });
      }

      onComplete && onComplete();
    } catch (error) {
      console.error('Error setting winning word:', error);
    }
  };

  const getVoteCount = (word) => {
    return Object.values(votes).filter(v => v === word).length;
  };

  // Find leading word(s) for highlighting
  const getLeadingWords = () => {
    const voteCounts = {};
    Object.values(votes).forEach(word => {
      voteCounts[word] = (voteCounts[word] || 0) + 1;
    });

    if (Object.keys(voteCounts).length === 0) return [];

    const maxVotes = Math.max(...Object.values(voteCounts));
    return Object.entries(voteCounts)
      .filter(([word, count]) => count === maxVotes)
      .map(([word]) => word);
  };

  const leadingWords = getLeadingWords();
  const votedCount = Object.keys(votes).length;

  return (
    <div style={{
      background: 'white',
      borderRadius: 'var(--radius-xl)',
      padding: '1.5rem',
      boxShadow: 'var(--shadow-md)'
    }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: 'var(--primary)',
          marginBottom: '0.5rem'
        }}>
          🎭 Team {wordSelectingTeamLabel}: Select Your Word
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          {isTieBreaker
            ? "⚖️ It's a tie! Change your vote to break it!"
            : "Tap a word to vote. You can change your vote anytime!"
          }
        </p>

        {/* Timer */}
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: isTieBreaker ? '#fef3c7' : (timeLeft <= 10 ? '#fee2e2' : 'var(--bg-tertiary)'),
          borderRadius: 'var(--radius)',
          textAlign: 'center',
          fontWeight: 600,
          color: isTieBreaker ? '#92400e' : (timeLeft <= 10 ? '#991b1b' : 'var(--text-primary)')
        }}>
          {isTieBreaker ? '⚖️ TIE BREAKER! ' : '⏱️ '}
          {timeLeft}s remaining
          {timeLeft <= 10 && !isTieBreaker && ' - Hurry!'}
        </div>
      </div>

      {/* Word Options */}
      <div style={{
        display: 'grid',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        {wordOptions.map((word, index) => {
          const voteCount = getVoteCount(word);
          const isSelected = selectedWord === word;
          const isLeading = leadingWords.includes(word) && voteCount > 0;
          const isTied = isLeading && leadingWords.length > 1;

          return (
            <button
              key={index}
              onClick={() => handleVote(word)}
              style={{
                padding: '1rem',
                borderRadius: 'var(--radius-lg)',
                border: `2px solid ${isSelected ? 'var(--primary)' : isLeading ? '#10b981' : 'var(--border)'}`,
                background: isSelected
                  ? 'rgba(99, 102, 241, 0.1)'
                  : isLeading
                    ? (isTied ? 'rgba(234, 179, 8, 0.1)' : 'rgba(16, 185, 129, 0.1)')
                    : 'white',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{
                  fontWeight: 600,
                  color: isSelected ? 'var(--primary)' : 'var(--text-primary)',
                  fontSize: '1rem'
                }}>
                  {isSelected && '✓ '}{word}
                  {isLeading && !isTied && ' 🏆'}
                  {isTied && ' ⚖️'}
                </div>
                {voteCount > 0 && (
                  <div style={{
                    background: isTied ? '#eab308' : (isLeading ? '#10b981' : 'var(--primary)'),
                    color: 'white',
                    borderRadius: '999px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 700
                  }}>
                    {voteCount}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom Word Input */}
      {allowCustomWords && (
        <div style={{
          padding: '1rem',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            fontWeight: 600,
            fontSize: '0.875rem',
            marginBottom: '0.5rem'
          }}>
            Add Your Own Word
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Enter a custom word..."
              value={customWord}
              onChange={(e) => setCustomWord(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCustomWord()}
              style={{ marginBottom: 0 }}
            />
            <button
              onClick={handleAddCustomWord}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Vote Status */}
      {selectedWord ? (
        <div style={{
          padding: '1rem',
          background: '#d1fae5',
          color: '#065f46',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          fontWeight: 600
        }}>
          ✓ Your vote: "{selectedWord}" - Tap another word to change
        </div>
      ) : (
        <div style={{
          padding: '1rem',
          background: '#fef3c7',
          color: '#92400e',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          fontWeight: 600
        }}>
          👆 Tap a word above to vote
        </div>
      )}

      {/* Voting Progress */}
      <div style={{
        marginTop: '1rem',
        fontSize: '0.875rem',
        color: 'var(--text-secondary)',
        textAlign: 'center'
      }}>
        {votedCount} / {teamMembers.length} team members have voted
      </div>
    </div>
  );
};

export default WordSelectionPhase;
